'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { CircleCheck, FileText, Mail, UserRound } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { isError } from '@/utilities/serverError';
import { cn } from '@/utilities/ui';
import { useLocalization } from '../../_providers/localization/LocalizationProvider';
import { sendInquiryEmail } from './actions';
type ContactType = 'expert' | 'quote';
type ContactButtonProps = {
  url: string;
  className?: string;
};
export function ContactButton(t0) {
  const $ = _c(50);
  const {
    url,
    className
  } = t0;
  const {
    messages: i18n
  } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [contactType, setContactType] = useState("expert");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [isSent, setIsSent] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mutationFn: _temp,
      onSuccess: () => {
        setIsSent(true);
        setTimeout(() => {
          setIsOpen(false);
          setIsSent(false);
        }, 3000);
      },
      onError: _temp2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const sendInquiryMutation = useMutation(t1);
  const isSending = sendInquiryMutation.isPending;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setContactType("expert");
      setContactMessage("");
      setIsSent(false);
      setIsOpen(true);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const openModal = t2;
  let t3;
  if ($[2] !== i18n.chat.contact.quotePrefill) {
    t3 = type => {
      setContactType(type);
      if (type === "expert") {
        setContactMessage("");
        return;
      }
      setContactMessage(i18n.chat.contact.quotePrefill);
    };
    $[2] = i18n.chat.contact.quotePrefill;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const onTypeChange = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = open => {
      setIsOpen(open);
      if (!open) {
        setIsSent(false);
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const onOpenChange = t4;
  let t5;
  if ($[5] !== contactEmail || $[6] !== contactMessage || $[7] !== i18n.chat.contact.invalidEmail || $[8] !== i18n.chat.contact.requiredFields || $[9] !== sendInquiryMutation || $[10] !== url) {
    t5 = async () => {
      const email = contactEmail.trim();
      const message = contactMessage.trim();
      if (!email || !message) {
        toast.error(i18n.chat.contact.requiredFields);
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error(i18n.chat.contact.invalidEmail);
        return;
      }
      await sendInquiryMutation.mutateAsync({
        email,
        url,
        message
      });
    };
    $[5] = contactEmail;
    $[6] = contactMessage;
    $[7] = i18n.chat.contact.invalidEmail;
    $[8] = i18n.chat.contact.requiredFields;
    $[9] = sendInquiryMutation;
    $[10] = url;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleSubmit = t5;
  const t6 = i18n.chat.contact.button;
  let t7;
  if ($[12] !== className) {
    t7 = cn("h-8 rounded-full bg-blue-400 px-4 text-xs font-medium text-white hover:bg-blue-400/90 flex gap-1.5", className);
    $[12] = className;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Mail className="h-3.5 w-3.5" />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== i18n.chat.contact.button) {
    t9 = <span className="hidden sm:inline">{i18n.chat.contact.button}</span>;
    $[15] = i18n.chat.contact.button;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== i18n.chat.contact.button || $[18] !== t7 || $[19] !== t9) {
    t10 = <Button type="button" onClick={openModal} title={t6} className={t7} size="auto">{t8}{t9}</Button>;
    $[17] = i18n.chat.contact.button;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = !isSending;
  let t12;
  if ($[21] !== contactEmail || $[22] !== contactMessage || $[23] !== contactType || $[24] !== handleSubmit || $[25] !== i18n.chat.contact.cancel || $[26] !== i18n.chat.contact.email || $[27] !== i18n.chat.contact.emailPlaceholder || $[28] !== i18n.chat.contact.expert || $[29] !== i18n.chat.contact.expertPlaceholder || $[30] !== i18n.chat.contact.message || $[31] !== i18n.chat.contact.quote || $[32] !== i18n.chat.contact.send || $[33] !== i18n.chat.contact.sending || $[34] !== i18n.chat.contact.sentText || $[35] !== i18n.chat.contact.sentTitle || $[36] !== i18n.chat.contact.title || $[37] !== isSending || $[38] !== isSent || $[39] !== onTypeChange) {
    t12 = isSent ? <div className="flex flex-col items-center px-6 py-10 text-center"><div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10 text-green-500"><CircleCheck className="h-6 w-6" /></div><DialogTitle className="text-center">{i18n.chat.contact.sentTitle}</DialogTitle><DialogDescription className="mt-2 text-center">{i18n.chat.contact.sentText}</DialogDescription></div> : <><DialogHeader className="border-border border-b px-6 py-5"><DialogTitle>{i18n.chat.contact.title}</DialogTitle></DialogHeader><div className="grid gap-4 px-6 py-5"><div className="grid grid-cols-2 gap-2"><Button type="button" variant="outline" onClick={() => onTypeChange("expert")} className={cn("h-10 justify-center gap-2 rounded-lg text-sm", contactType === "expert" && "bg-muted text-foreground border-primary")}><UserRound className="h-4 w-4" />{i18n.chat.contact.expert}</Button><Button type="button" variant="outline" onClick={() => onTypeChange("quote")} className={cn("h-10 justify-center gap-2 rounded-lg text-sm", contactType === "quote" && "bg-muted text-foreground border-primary")}><FileText className="h-4 w-4" />{i18n.chat.contact.quote}</Button></div><div className="grid gap-2"><label className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{i18n.chat.contact.email}</label><Input type="email" value={contactEmail} onChange={event => setContactEmail(event.target.value)} placeholder={i18n.chat.contact.emailPlaceholder} className="h-10 rounded-lg" /></div><div className="grid gap-2"><label className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{i18n.chat.contact.message}</label><Textarea value={contactMessage} onChange={event_0 => setContactMessage(event_0.target.value)} placeholder={contactType === "expert" ? i18n.chat.contact.expertPlaceholder : ""} className="min-h-30 rounded-lg" /></div></div><DialogFooter className="px-6 pb-5 pt-0 sm:justify-end"><Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={isSending} className="rounded-full px-5">{i18n.chat.contact.cancel}</Button><Button type="button" onClick={handleSubmit} disabled={isSending} className="rounded-full px-5">{isSending ? i18n.chat.contact.sending : i18n.chat.contact.send}</Button></DialogFooter></>;
    $[21] = contactEmail;
    $[22] = contactMessage;
    $[23] = contactType;
    $[24] = handleSubmit;
    $[25] = i18n.chat.contact.cancel;
    $[26] = i18n.chat.contact.email;
    $[27] = i18n.chat.contact.emailPlaceholder;
    $[28] = i18n.chat.contact.expert;
    $[29] = i18n.chat.contact.expertPlaceholder;
    $[30] = i18n.chat.contact.message;
    $[31] = i18n.chat.contact.quote;
    $[32] = i18n.chat.contact.send;
    $[33] = i18n.chat.contact.sending;
    $[34] = i18n.chat.contact.sentText;
    $[35] = i18n.chat.contact.sentTitle;
    $[36] = i18n.chat.contact.title;
    $[37] = isSending;
    $[38] = isSent;
    $[39] = onTypeChange;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t11 || $[42] !== t12) {
    t13 = <DialogContent className="overflow-hidden p-0 sm:max-w-115" showCloseButton={t11}>{t12}</DialogContent>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== isOpen || $[45] !== t13) {
    t14 = <Dialog open={isOpen} onOpenChange={onOpenChange}>{t13}</Dialog>;
    $[44] = isOpen;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t10 || $[48] !== t14) {
    t15 = <>{t10}{t14}</>;
    $[47] = t10;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
}
function _temp2(error) {
  toast.error(error.message || "Failed to send email");
}
async function _temp(data) {
  const result = await sendInquiryEmail(data);
  if (isError(result)) {
    throw new Error(result.message || "Failed to send email");
  }
  return result;
}